import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { userApi } from '../../../services/user';
import type { IUserResponse } from '../../../common/interfaces/iUser.interface';

/** Channel surface from JWT; kept local until channel domain exists in this app. */
export type ChannelType = string | null;

const COMMON_STORAGE_KEY = 'common';
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

export const localStoreKey = COMMON_STORAGE_KEY;
export const localStoreKeyAccessToken = ACCESS_TOKEN_KEY;
export const localStoreKeyRefreshToken = REFRESH_TOKEN_KEY;

/** Decode JWT payload without verifying signature */
const decodeJwtPayload = (token: string): Record<string, any> => {
  try {
    const base64 = token.split('.')[1];
    return JSON.parse(atob(base64.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
};

export type UserRole = 'tenant' | 'none' | 'owner' | 'admin';

/**
 * Maps backend role string to UserRole type
 * Backend returns roles like "OWNER", "ADMIN", etc. (uppercase)
 */
const mapRoleToUserRole = (role: string | null | undefined): UserRole => {
  if (!role) return 'owner'; // Default fallback
  
  const normalizedRole = role.toLowerCase();
  
  // Map backend role names to frontend UserRole
  switch (normalizedRole) {
    case 'owner':
    case 'admin':
    case 'tenant':
      return normalizedRole as UserRole;
    case 'client': // Backend may return 'client' for 'stock_owner'
      return 'owner';
    default:
      return 'owner'; // Default fallback
  }
};

interface AuthorizationState {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    fullName?: string;
    avatar: string;
    role: UserRole;
  } | null;
  tenantID: string | null;
  isAdmin: boolean;
  accountId: number | null;
  channelType: ChannelType | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  error: string | null;
}

// Load initial state from localStorage
const loadInitialState = (): AuthorizationState => {
  try {
    const commonData = localStorage.getItem(COMMON_STORAGE_KEY);
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (commonData && accessToken) {
      const parsed = JSON.parse(commonData);
      const jwtPayload = decodeJwtPayload(accessToken);
      return {
        user: parsed.user || null,
        tenantID: parsed.tenantID || null,
        isAdmin: parsed.isAdmin || false,
        accountId: jwtPayload.accountId ?? null,
        channelType: jwtPayload.channelType ?? null,
        accessToken: accessToken || null,
        refreshToken: refreshToken || null,
        isLoading: false,
        error: null,
      };
    }
  } catch (err) {
    console.error('Error loading auth state from localStorage:', err);
  }

  return {
    user: null,
    tenantID: null,
    isAdmin: false,
    accountId: null,
    channelType: null,
    accessToken: null,
    refreshToken: null,
    isLoading: false,
    error: null,
  };
};

export const initialState: AuthorizationState = loadInitialState();

const authorizationSlice = createSlice({
  name: 'authorization',
  initialState,
  reducers: {
    logOut: (state, _action?: any) => {
      state.user = null;
      state.tenantID = null;
      state.isAdmin = false;
      state.accountId = null;
      state.channelType = null;
      state.accessToken = null;
      state.refreshToken = null;
      localStorage.removeItem(localStoreKey);
      localStorage.removeItem(localStoreKeyAccessToken);
      localStorage.removeItem(localStoreKeyRefreshToken);
      localStorage.removeItem('dynamic');
    },
    uploadUser: (state, action: PayloadAction<any>) => {
      state.user = action.payload;
    },
    setUser: (state, action: PayloadAction<AuthorizationState['user']>) => {
      state.user = action.payload;
    },
    setTenantID: (state, action: PayloadAction<string>) => {
      state.tenantID = action.payload;
    },
    setUserRole: (state, action: PayloadAction<UserRole>) => {
      if (state.user) {
        state.user.role = action.payload;
      }
    },
    clearError: state => {
      state.error = null;
    },
    setAuthorization: (state, action: PayloadAction<any>) => {
      const { user, tenantID, isAdmin, accessToken, refreshToken } =
        action.payload;

      localStorage.setItem(
        localStoreKey,
        JSON.stringify({
          user,
          tenantID,
          isAdmin,
        })
      );
      state.user = user;
      state.tenantID = tenantID;
      state.isAdmin = isAdmin;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;

      const jwtPayload = decodeJwtPayload(accessToken || '');
      state.accountId = jwtPayload.accountId ?? null;
      state.channelType = jwtPayload.channelType ?? null;

      localStorage.setItem(localStoreKeyAccessToken, state.accessToken || '');
      localStorage.setItem(localStoreKeyRefreshToken, state.refreshToken || '');
    },
    setTokens: (
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string }>
    ) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;

      const payload = decodeJwtPayload(action.payload.accessToken);
      state.accountId = payload.accountId ?? null;
      state.channelType = payload.channelType ?? null;

      localStorage.setItem(localStoreKeyAccessToken, state.accessToken);
      localStorage.setItem(localStoreKeyRefreshToken, state.refreshToken);
    },
    // getTokens закоментовано - reducer не може повертати значення
    // Використовуйте селектор selectTokens замість цього
  },
  extraReducers: builder => {
    builder
      .addMatcher(userApi.endpoints.login.matchPending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(
        userApi.endpoints.login.matchFulfilled,
        (state, { payload }: { payload: IUserResponse }) => {
          state.isLoading = false;
          state.error = null;

          console.log('🔍 [authorizationSlice] Received payload:', payload);
          console.log('🔍 [authorizationSlice] payload.user.role:', payload.user.role);
          console.log('🔍 [authorizationSlice] payload.isAdmin:', payload.isAdmin);

          // Set user data
          // Map backend role to UserRole type
          const userRole = mapRoleToUserRole(payload.user.role);
          console.log('🔍 [authorizationSlice] Mapped userRole:', userRole);
          
          state.user = {
            id: payload.user.id,
            email: payload.user.email,
            firstName: payload.user.firstName,
            lastName: payload.user.lastName,
            fullName:
              payload.user.fullName ||
              `${payload.user.firstName} ${payload.user.lastName}`,
            avatar: payload.user.avatar,
            role: payload.isAdmin ? 'admin' : userRole,
          };
          
          console.log('🔍 [authorizationSlice] Final state.user.role:', state.user.role);

          // Set tokens
          state.accessToken = payload.accessToken;
          state.refreshToken = payload.refreshToken;

          const jwtSignInPayload = decodeJwtPayload(payload.accessToken || '');
          state.accountId = jwtSignInPayload.accountId ?? null;
          state.channelType = jwtSignInPayload.channelType ?? null;

          // Set tenant and admin status
          state.tenantID = payload.tenantID;
          state.isAdmin = payload.isAdmin;

          // Save to localStorage
          localStorage.setItem(
            localStoreKey,
            JSON.stringify({
              user: state.user,
              tenantID: state.tenantID,
              isAdmin: state.isAdmin,
            })
          );
          localStorage.setItem(
            localStoreKeyAccessToken,
            state.accessToken || ''
          );
          localStorage.setItem(
            localStoreKeyRefreshToken,
            state.refreshToken || ''
          );
        }
      )
      .addMatcher(
        userApi.endpoints.login.matchRejected,
        (state, { error }) => {
          state.isLoading = false;
          state.error = error.message || 'Sign in failed';
        }
      )
      .addMatcher(userApi.endpoints.signUp.matchPending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(
        userApi.endpoints.signUp.matchFulfilled,
        (state, { payload }: { payload: IUserResponse }) => {
          state.isLoading = false;
          state.error = null;

          // Set user data
          // Map backend role to UserRole type
          const userRole = mapRoleToUserRole(payload.user.role);
          
          state.user = {
            id: payload.user.id,
            email: payload.user.email,
            firstName: payload.user.firstName,
            lastName: payload.user.lastName,
            fullName:
              payload.user.fullName ||
              `${payload.user.firstName} ${payload.user.lastName}`,
            avatar: payload.user.avatar,
            role: payload.isAdmin ? 'admin' : userRole,
          };

          // Set tokens
          state.accessToken = payload.accessToken;
          state.refreshToken = payload.refreshToken;

          const jwtSignUpPayload = decodeJwtPayload(payload.accessToken || '');
          state.accountId = jwtSignUpPayload.accountId ?? null;
          state.channelType = jwtSignUpPayload.channelType ?? null;

          // Set tenant and admin status
          state.tenantID = payload.tenantID;
          state.isAdmin = payload.isAdmin;

          // Save to localStorage
          localStorage.setItem(
            localStoreKey,
            JSON.stringify({
              user: state.user,
              tenantID: state.tenantID,
              isAdmin: state.isAdmin,
            })
          );
          localStorage.setItem(
            localStoreKeyAccessToken,
            state.accessToken || ''
          );
          localStorage.setItem(
            localStoreKeyRefreshToken,
            state.refreshToken || ''
          );
        }
      )
      .addMatcher(
        userApi.endpoints.signUp.matchRejected,
        (state, { error }) => {
          state.isLoading = false;
          state.error = error.message || 'Sign up failed';
        }
      )
      /* .addMatcher(
        userApi.endpoints.updateProfile.matchFulfilled,
        (state, { payload }: { payload: any }) => {
          if (state.user) {
            state.user = {
              ...state.user,
              firstName: payload.firstName || state.user.firstName,
              lastName: payload.lastName || state.user.lastName,
              email: payload.email || state.user.email,
              avatar: payload.avatar || state.user.avatar,
              fullName: `${payload.firstName} ${payload.lastName}`,
            };

            const commonData = localStorage.getItem(localStoreKey);
            if (commonData) {
              const parsed = JSON.parse(commonData);
              parsed.user = state.user;
              localStorage.setItem(localStoreKey, JSON.stringify(parsed));
            }
          }
        }
      ) */;
  },
});

export default authorizationSlice.reducer;

export const {
  setTokens,
  logOut,
  uploadUser,
  setAuthorization,
  setUser,
  setTenantID,
  setUserRole,
  clearError,
} = authorizationSlice.actions;

// Селектори для authorization
export const selectAuthorization = (state: any) => state.authorization;
export const selectAuthorizationUser = (state: any) =>
  state.authorization?.user;
export const selectAuthorizationIsAdmin = (state: any) =>
  state.authorization?.isAdmin;
export const selectAuthorizationTenantID = (state: any) =>
  state.authorization?.tenantID;
export const selectAuthorizationIsLoading = (state: any) =>
  state.authorization?.isLoading;
export const selectAuthorizationError = (state: any) =>
  state.authorization?.error;
export const selectTokens = (state: any) => ({
  accessToken: state.authorization?.accessToken,
  refreshToken: state.authorization?.refreshToken,
});
export const selectAccessToken = (state: any) =>
  state.authorization?.accessToken;
export const selectRefreshToken = (state: any) =>
  state.authorization?.refreshToken;
export const selectUserRole = (state: any) =>
  state.authorization?.user?.role || 'none';
export const selectAccountId = (state: any) =>
  state.authorization?.accountId as number | null;
export const selectChannelType = (state: any) =>
  state.authorization?.channelType as ChannelType;
